import React, { useState, useRef } from "react";
import Checkbox from "@mui/material/Checkbox";
import ListItemText from "@mui/material/ListItemText";
import {
  MenuItem,
  InputLabel,
  Select,
  Box,
  Skeleton,
  TextField,
} from "@mui/material";

const MultiSelectOption = ({
  label,
  labelId,
  options,
  placeholder,
  dropdownWidth,
  selectedOptions,
  setSelectedOptions,
  isLoading,
} = {}) => {
  const [isTouched, setIsTouched] = useState(false);
  const [searchText, setSearchText] = useState("");
  const searchInputRef = useRef(null);

  const handleDropdownClick = () => {
    setIsTouched(true);
  };

  const handleDropdownOpen = () => {
    setTimeout(() => {
      if (searchInputRef.current) {
        searchInputRef.current.focus();
      }
    }, 10);
  };

  const handleChange = (event) => {
    setSelectedOptions(event.target.value);
  };

  const handleSearchChange = (event) => {
    setSearchText(event.target.value);
  };

  const filteredOptions = options.filter((option) =>
    option.name.toLowerCase().includes(searchText.toLowerCase())
  );

  return (
    <>
      <InputLabel id={labelId}>{label}</InputLabel>
      {isLoading ? (
        <Box display="flex" alignItems="center" gap={2}>
          <Skeleton variant="rectangular" width={140} height={40} />
        </Box>
      ) : (
        <>
          <Select
            displayEmpty
            renderValue={(selected) => {
              if (selected.length === 0) {
                return <span style={{ color: "#aaa" }}>{placeholder}</span>;
              }
              return selected
                .map((id) => {
                  const option = options.find((o) => o.id === id);
                  return option ? option.name : "";
                })
                .join(", ");
            }}
            multiple
            value={selectedOptions}
            onChange={(event) => {
              if (
                event.target.value &&
                !event.target.value.includes(undefined)
              ) {
                handleChange(event);
              }
            }}
            onClick={handleDropdownClick}
            onOpen={handleDropdownOpen}
            size="small"
            style={dropdownWidth ? { width: dropdownWidth } : undefined}
            MenuProps={{
              anchorOrigin: {
                vertical: "bottom",
                horizontal: "left",
              },
              transformOrigin: {
                vertical: "top",
                horizontal: "left",
              },
              getContentAnchorEl: null,
              marginThreshold: 5,
            }}
          >
            {/* 1. Add TextField for filtering as the first MenuItem */}
            <MenuItem
              // IMPORTANT: Stop event propagation to keep the dropdown open when interacting with the TextField
              onKeyDown={(e) => e.stopPropagation()}
              onMouseDown={(e) => e.stopPropagation()}
              disableRipple
              disableTouchRipple
              style={{
                cursor: "default",
                padding: "8px 16px",
                backgroundColor: "white",
                position: "sticky",
                top: 0,
                zIndex: 1,
              }}
            >
              <TextField
                inputRef={searchInputRef}
                fullWidth
                label={`Search ${label}`}
                variant="outlined"
                size="small"
                value={searchText}
                onChange={handleSearchChange}
              />
            </MenuItem>

            {/* 2. Select/Unselect All option operates only on the currently filtered list */}
            {filteredOptions.length > 0 && (
              <MenuItem
                onMouseDown={(event) => {
                  event.preventDefault();
                  // Check selection status against the filtered list
                  const allSelectedFiltered = filteredOptions.every((option) =>
                    selectedOptions.includes(option.id)
                  );

                  let newSelectedOptions;
                  if (allSelectedFiltered) {
                    // Unselect all currently filtered options
                    newSelectedOptions = selectedOptions.filter(
                      (id) => !filteredOptions.map((o) => o.id).includes(id)
                    );
                  } else {
                    // Select all currently filtered options, without duplicating existing selections
                    const filteredIds = filteredOptions.map(
                      (option) => option.id
                    );
                    const existingNonFilteredIds = selectedOptions.filter(
                      (id) => !filteredIds.includes(id)
                    );
                    newSelectedOptions = [
                      ...existingNonFilteredIds,
                      ...filteredIds,
                    ];
                  }

                  setSelectedOptions(newSelectedOptions);
                }}
                style={{ fontWeight: "bold", color: "#1976d2" }}
              >
                {filteredOptions.every((option) =>
                  selectedOptions.includes(option.id)
                )
                  ? `Unselect All (${filteredOptions.length})`
                  : `Select All (${filteredOptions.length})`}
              </MenuItem>
            )}

            {/* 3. Render filtered options */}
            {filteredOptions.map((option) => (
              <MenuItem key={option.id} value={option.id}>
                <Checkbox checked={selectedOptions.indexOf(option.id) > -1} />
                <ListItemText primary={option.name} />
              </MenuItem>
            ))}

            {/* 4. No results message */}
            {filteredOptions.length === 0 && searchText.length > 0 && (
              <MenuItem disabled>
                <ListItemText primary={`No results for "${searchText}"`} />
              </MenuItem>
            )}
          </Select>
          <Box>
            {selectedOptions?.length === 0 && isTouched && (
              <span style={{ color: "red", fontSize: "12px" }}>
                {label} is required
              </span>
            )}
          </Box>
        </>
      )}
    </>
  );
};

export default MultiSelectOption;
